import { type ClerkProvider } from '@clerk/nextjs';
import { type ComponentProps } from 'react';
import { authCodeGroupStyle, authCodeSlotStyle } from './auth-control-styles';

type ClerkAppearance = NonNullable<ComponentProps<typeof ClerkProvider>['appearance']>;

// Retained for Clerk account settings and required security challenge UI.
export const clerkAppearance: ClerkAppearance = {
  options: {
    showOptionalFields: true,
  },
  variables: {
    colorPrimary: 'hsl(var(--primary))',
    colorForeground: '#2f2925',
    colorMutedForeground: '#64748b',
    colorBackground: '#ffffff',
    colorInput: '#ffffff',
    colorInputForeground: '#2f2925',
    colorDanger: '#dc2626',
    colorSuccess: '#2e8e3e',
    fontFamily: 'inherit',
    fontFamilyButtons: 'inherit',
    fontSize: '0.875rem',
    borderRadius: '0.375rem',
  },
  userProfile: {
    elements: {
      modalContent: {
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100dvh - 3rem)',
        minHeight: 0,
      },
      rootBox: {
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100dvh - 3rem)',
      },
      cardBox: {
        height: 'min(44rem, calc(100dvh - 3rem))',
        maxHeight: 'calc(100dvh - 3rem)',
        maxWidth: '100%',
        minHeight: 0,
      },
      scrollBox: {
        minHeight: 0,
        maxHeight: '100%',
      },
      pageScrollBox: {
        minHeight: 0,
        overflowY: 'auto',
      },
    },
  },
  elements: {
    cardBox: {
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    },
    card: {
      backgroundColor: '#ffffff',
      boxShadow: 'none',
    },
    headerTitle: { color: '#2f2925', fontWeight: 700 },
    headerSubtitle: { color: '#64748b' },
    formFieldLabel: { fontSize: '0.875rem', fontWeight: 500 },
    formFieldInput: {
      minHeight: '2.25rem',
      borderRadius: '0.375rem',
      borderColor: '#e2e8f0',
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      '&:focus': { borderColor: 'hsl(var(--primary))' },
      '&[aria-invalid="true"]': { borderColor: '#dc2626' },
    },
    otpCodeFieldInputs: authCodeGroupStyle,
    otpCodeFieldInput: {
      ...authCodeSlotStyle,
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    },
    formButtonPrimary: {
      minHeight: '2.5rem',
      backgroundColor: 'hsl(var(--primary))',
      backgroundImage: 'none',
      color: '#ffffff',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '0.375rem',
      boxShadow: 'none',
      '&:hover': { backgroundColor: 'hsl(var(--primary) / 0.9)' },
      '&:active': { backgroundColor: 'hsl(var(--primary) / 0.9)' },
      '&:disabled': { opacity: 0.5 },
    },
    socialButtonsBlockButton: {
      minHeight: '2.5rem',
      borderColor: '#e2e8f0',
      borderRadius: '0.375rem',
      boxShadow: 'none',
      '&:hover': { backgroundColor: '#f1f5f9' },
    },
    dividerLine: { backgroundColor: '#e2e8f0' },
    userButtonTrigger: {
      padding: '0.25rem',
      borderRadius: '0.375rem',
      boxShadow: 'none',
      '&:hover': { backgroundColor: '#f1f5f9' },
      '&:focus': { boxShadow: 'none' },
      '&:focus-visible': { outline: '2px solid #38b148', outlineOffset: '2px' },
    },
    userButtonBox: { gap: '0.5rem' },
    userButtonAvatarBox: { width: '2rem', height: '2rem', backgroundColor: '#e5e7eb' },
    userButtonOuterIdentifier: {
      color: '#374151',
      fontSize: '0.875rem',
      fontWeight: 500,
      maxWidth: '8rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '@media (max-width: 639px)': { display: 'none' },
    },
    userButtonPopoverCard: {
      width: '20rem',
      maxWidth: 'calc(100vw - 2rem)',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    },
    userButtonPopoverMain: { backgroundColor: '#ffffff' },
    userPreviewMainIdentifier: { color: '#2f2925', fontWeight: 500 },
    userPreviewSecondaryIdentifier: { color: '#6b7280', fontSize: '0.75rem' },
    userButtonPopoverActions: { borderColor: '#e2e8f0' },
    userButtonPopoverActionButton: {
      color: '#374151',
      fontSize: '0.875rem',
      borderColor: '#e2e8f0',
      '&:hover': { backgroundColor: '#f1f5f9' },
      '&:focus-visible': { outline: '2px solid #38b148', outlineOffset: '-2px' },
    },
    userButtonPopoverActionButton__signOut: { color: '#dc2626' },
    userButtonPopoverActionButton__signOutAll: { color: '#dc2626' },
    userButtonPopoverActionButtonIcon__signOut: { color: '#dc2626' },
    userButtonPopoverActionButtonIcon__signOutAll: { color: '#dc2626' },
  },
};
